/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { RightOutlined } from '@ant-design/icons';
import { useState } from 'react';

interface SchemaViewerProps {
  schema: Record<string, unknown>;
}

function formatType(schema: Record<string, unknown>): string {
  if (schema.type === 'array') {
    const items = schema.items as Record<string, unknown>;
    if (items?.type) return `${items.type as string}[]`;
    if (items?.title) return `${items.title as string}[]`;
    return 'array';
  }
  if (schema.enum) return (schema.enum as string[]).join(' | ');
  if (schema.format) return `${schema.type as string} (${schema.format as string})`;
  return (schema.type as string) || 'object';
}

function descriptionText(prop: Record<string, unknown>): string | null {
  if (typeof prop.description === 'string') return prop.description;
  if (prop.example !== undefined) return `e.g. ${JSON.stringify(prop.example)}`;
  return null;
}

interface PropertyRowProps {
  name: string;
  prop: Record<string, unknown>;
  required: boolean;
  depth: number;
}

function PropertyRow({ name, prop, required, depth }: PropertyRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = Boolean(
    (prop.type === 'object' && prop.properties) ||
      (prop.type === 'array' && (prop.items as Record<string, unknown>)?.properties),
  );

  const childSchema = prop.type === 'array' ? (prop.items as Record<string, unknown>) : prop;
  const description = descriptionText(prop);

  return (
    <>
      <tr className="apiref-schema-row">
        <td className="apiref-schema-cell-name" style={{ paddingLeft: 12 + depth * 24 }}>
          {hasChildren ? (
            <button
              type="button"
              className="apiref-schema-toggle"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${name}`}
            >
              <RightOutlined className={`apiref-schema-chevron ${expanded ? 'open' : ''}`} />
            </button>
          ) : (
            <span className="apiref-schema-spacer" />
          )}
          <code>{name}</code>
        </td>
        <td className="apiref-schema-cell-type">{formatType(prop)}</td>
        <td className="apiref-schema-cell-req">
          {required && <span className="apiref-schema-req">required</span>}
          {Boolean(prop.nullable) && <span className="apiref-schema-nullable">nullable</span>}
        </td>
        <td className="apiref-schema-cell-desc">{description && <code>{description}</code>}</td>
      </tr>
      {expanded &&
        hasChildren &&
        childSchema?.properties &&
        Object.entries(childSchema.properties as Record<string, Record<string, unknown>>).map(
          ([childName, childProp]) => (
            <PropertyRow
              key={childName}
              name={childName}
              prop={childProp}
              required={((childSchema.required as string[]) || []).includes(childName)}
              depth={depth + 1}
            />
          ),
        )}
    </>
  );
}

export function SchemaViewer({ schema }: SchemaViewerProps) {
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  const required = (schema.required as string[]) || [];

  if (!properties) {
    return (
      <div className="apiref-schema-empty">
        <span className="apiref-schema-type">{formatType(schema)}</span>
      </div>
    );
  }

  return (
    <table className="apiref-schema-table">
      <thead>
        <tr>
          <th>Field</th>
          <th>Type</th>
          <th>Required</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(properties).map(([name, prop]) => (
          <PropertyRow key={name} name={name} prop={prop} required={required.includes(name)} depth={0} />
        ))}
      </tbody>
    </table>
  );
}
